import { get, push, ref, update } from "firebase/database";
import { getSocialAccountKey, normalizeSocialAccount } from "./socialChannels";

export const CRON_POST_SUMMARY_ROOT = "cronPostConfigSummaries";
export const PRODUCT_AUTO_POST_CONFIG_SUMMARY_ROOT =
  "productAutoPostConfigSummaries";
export const PRODUCT_AUTO_POST_CONFIG_SUMMARY_REF_ROOT =
  "productAutoPostConfigSummaryRefs";
export const PLATFORM_FACEBOOK = "facebook";
export const PLATFORM_ZALO = "zalo";
export const PLATFORM_WORDPRESS = "wordpress";
export const POST_SUMMARY_VERSION = 1;
export const PRODUCT_AUTO_POST_SOURCE_LOCAL = "localProducts";
export const PRODUCT_AUTO_POST_SOURCE_WOO = "wooProducts";
export const CRON_SUMMARY_PLATFORMS = [
  PLATFORM_FACEBOOK,
  PLATFORM_ZALO,
  PLATFORM_WORDPRESS,
];

const normalizeBoolean = (value) => value === true;
const normalizeProductSource = (value) => String(value || "").trim();

export const isWooProductAutoPostConfig = (config = {}) => {
  const productSource = normalizeProductSource(config?.productSource);
  const sourceType = normalizeProductSource(config?.sourceType);

  return (
    productSource === PRODUCT_AUTO_POST_SOURCE_WOO ||
    sourceType === PRODUCT_AUTO_POST_SOURCE_WOO ||
    Boolean(config?.wordpressSiteId) ||
    (Array.isArray(config?.selectedWooProductIds) &&
      config.selectedWooProductIds.length > 0)
  );
};

export const isLocalProductAutoPostConfig = (config = {}) => {
  if (config?.inputType !== "products") {
    return false;
  }

  const productSource = normalizeProductSource(config?.productSource);
  const sourceType = normalizeProductSource(config?.sourceType);

  if (productSource === PRODUCT_AUTO_POST_SOURCE_WOO) {
    return false;
  }

  if (isWooProductAutoPostConfig(config)) {
    return false;
  }

  if (
    productSource === PRODUCT_AUTO_POST_SOURCE_LOCAL ||
    sourceType === "products"
  ) {
    return true;
  }

  return !isWooProductAutoPostConfig(config);
};

export const normalizePlatform = (config = {}) =>
  String(config?.platform || "").toLowerCase() === PLATFORM_WORDPRESS
    ? PLATFORM_WORDPRESS
    : String(config?.platform || "").toLowerCase() === PLATFORM_ZALO
      ? PLATFORM_ZALO
      : PLATFORM_FACEBOOK;

const getOtherPlatforms = (platform) =>
  CRON_SUMMARY_PLATFORMS.filter((item) => item !== platform);

const HEAVY_POST_CONFIG_FIELDS = new Set(["fanpageProducts"]);

export const buildPlatformEnabledKey = (config = {}) => {
  const platform = normalizePlatform(config);
  return `${platform}:${normalizeBoolean(config?.autoPostEnabled)}`;
};

export const buildPostCronSummaryId = (userId, postId) =>
  `${encodeURIComponent(String(userId))}__${encodeURIComponent(String(postId))}`;

const normalizeCronEligible = (options = {}) =>
  typeof options.cronEligible === "boolean" ? options.cronEligible : true;

export const buildPostCronSummary = (
  userId,
  postId,
  config = {},
  options = {}
) => {
  const platform = normalizePlatform(config);
  const autoPostEnabled = normalizeBoolean(config?.autoPostEnabled);
  const cronEligible = normalizeCronEligible(options);
  const summary = {
    userId,
    postId,
    platform,
    inputType: config?.inputType || "topic",
    autoPostEnabled,
    platformEnabledKey: `${platform}:${autoPostEnabled}`,
    time: config?.time ?? null,
    times: Array.isArray(config?.times) ? config.times : null,
    selectedDays:
      config?.selectedDays && typeof config.selectedDays === "object"
        ? config.selectedDays
        : null,
    scheduleMode: config?.scheduleMode || "weekly",
    fixedDateSchedules: Array.isArray(config?.fixedDateSchedules)
      ? config.fixedDateSchedules
      : null,
    updatedAt: config?.updatedAt || Date.now(),
    summaryVersion: POST_SUMMARY_VERSION,
  };

  summary.cronEligible = cronEligible;
  summary.platformEnabledEligibleKey = `${platform}:${autoPostEnabled}:${cronEligible}`;

  return summary;
};

export const getProductAutoPostSummaryAccount = (config = {}) => {
  if (
    Array.isArray(config?.selectedAccounts) &&
    config.selectedAccounts.length > 0
  ) {
    return normalizeSocialAccount(
      config.selectedAccounts[0],
      config.selectedAccounts[0]?.platform || config.platform || "facebook"
    );
  }

  if (
    Array.isArray(config?.selectedFanpages) &&
    config.selectedFanpages.length > 0
  ) {
    return normalizeSocialAccount(config.selectedFanpages[0], "facebook");
  }

  if (config?.fanpageID || config?.fanpageId) {
    return normalizeSocialAccount(
      {
        platform: "facebook",
        accountId: config.fanpageID || config.fanpageId,
        id: config.fanpageID || config.fanpageId,
        name: config.fanpageName || "",
      },
      "facebook"
    );
  }

  return null;
};

export const buildProductAutoPostConfigSummary = (postId, config = {}) => {
  const platform = normalizePlatform(config);
  if (
    config?.inputType !== "products" ||
    ![PLATFORM_FACEBOOK, PLATFORM_ZALO].includes(platform) ||
    !isLocalProductAutoPostConfig(config)
  ) {
    return null;
  }

  const account = getProductAutoPostSummaryAccount(config);
  if (!account?.accountId) {
    return null;
  }

  return {
    postId,
    platform,
    accountId: account.accountId,
    accountName: account.name || "",
    accountKey: getSocialAccountKey(account),
    productSource: PRODUCT_AUTO_POST_SOURCE_LOCAL,
    autoPostEnabled: normalizeBoolean(config?.autoPostEnabled),
    postMode: platform === PLATFORM_ZALO ? "direct" : config?.postMode || "direct",
    times: Array.isArray(config?.times) ? config.times : null,
    selectedDays:
      config?.selectedDays && typeof config.selectedDays === "object"
        ? config.selectedDays
        : null,
    scheduleMode: config?.scheduleMode || "weekly",
    fixedDateSchedules: Array.isArray(config?.fixedDateSchedules)
      ? config.fixedDateSchedules
      : null,
    updatedAt: config?.updatedAt || Date.now(),
    summaryVersion: POST_SUMMARY_VERSION,
  };
};

export const buildProductAutoPostSummaryUpdates = (userId, postId, config = {}) => {
  const summary = buildProductAutoPostConfigSummary(postId, config);
  if (!summary) {
    return {
      [`${PRODUCT_AUTO_POST_CONFIG_SUMMARY_REF_ROOT}/${userId}/${postId}`]: null,
    };
  }

  return {
    [`${PRODUCT_AUTO_POST_CONFIG_SUMMARY_ROOT}/${userId}/${summary.accountKey}/${postId}`]:
      summary,
    [`${PRODUCT_AUTO_POST_CONFIG_SUMMARY_REF_ROOT}/${userId}/${postId}`]: {
      accountKey: summary.accountKey,
    },
  };
};

export const buildProductAutoPostSummaryDeleteUpdates = (
  userId,
  postId,
  accountKey
) => {
  const updates = {
    [`${PRODUCT_AUTO_POST_CONFIG_SUMMARY_REF_ROOT}/${userId}/${postId}`]: null,
  };

  if (accountKey) {
    updates[
      `${PRODUCT_AUTO_POST_CONFIG_SUMMARY_ROOT}/${userId}/${accountKey}/${postId}`
    ] = null;
  }

  return updates;
};

export const buildPostCronIndexUpdates = (
  userId,
  postId,
  config = {},
  options = {}
) => {
  const platform = normalizePlatform(config);
  const summaryId = buildPostCronSummaryId(userId, postId);
  const summary = buildPostCronSummary(userId, postId, config, options);
  const updates = {
    [`posts/${userId}/${postId}/platformEnabledKey`]: summary.platformEnabledKey,
    [`${CRON_POST_SUMMARY_ROOT}/${platform}/${summaryId}`]: summary,
  };

  getOtherPlatforms(platform).forEach((otherPlatform) => {
    updates[`${CRON_POST_SUMMARY_ROOT}/${otherPlatform}/${summaryId}`] = null;
  });

  return updates;
};

export const buildPostCronIndexDeleteUpdates = (userId, postId) => {
  const summaryId = buildPostCronSummaryId(userId, postId);

  return {
    [`${CRON_POST_SUMMARY_ROOT}/${PLATFORM_FACEBOOK}/${summaryId}`]: null,
    [`${CRON_POST_SUMMARY_ROOT}/${PLATFORM_ZALO}/${summaryId}`]: null,
    [`${CRON_POST_SUMMARY_ROOT}/${PLATFORM_WORDPRESS}/${summaryId}`]: null,
  };
};

export const buildPostConfigUpsertUpdates = (
  userId,
  postId,
  config = {},
  options = {}
) => {
  const updates = {};

  Object.entries(config).forEach(([key, value]) => {
    if (value !== undefined && !HEAVY_POST_CONFIG_FIELDS.has(key)) {
      updates[`posts/${userId}/${postId}/${key}`] = value;
    }
  });

  return {
    ...updates,
    ...buildPostCronIndexUpdates(userId, postId, config, options),
    ...buildProductAutoPostSummaryUpdates(userId, postId, config),
  };
};

const getExistingPostCronEligibility = async (database, userId, postId) => {
  const summaryId = buildPostCronSummaryId(userId, postId);
  const snapshots = await Promise.all(
    CRON_SUMMARY_PLATFORMS.map((platform) =>
      get(ref(database, `${CRON_POST_SUMMARY_ROOT}/${platform}/${summaryId}`))
        .then((snapshot) => (snapshot?.exists() ? snapshot.val() : null))
        .catch(() => null)
    )
  );
  const summary = snapshots.find(
    (item) => item && typeof item.cronEligible === "boolean"
  );
  return typeof summary?.cronEligible === "boolean"
    ? summary.cronEligible
    : undefined;
};

export const savePostConfigWithCronIndex = async (
  database,
  userId,
  postId,
  config = {}
) => {
  if (postId) {
    const previousRefSnapshot = await get(
      ref(database, `${PRODUCT_AUTO_POST_CONFIG_SUMMARY_REF_ROOT}/${userId}/${postId}`)
    ).catch(() => null);
    const previousAccountKey = previousRefSnapshot?.exists()
      ? previousRefSnapshot.val()?.accountKey
      : null;
    const nextSummary = buildProductAutoPostConfigSummary(postId, config);
    const staleSummaryDeleteUpdates =
      previousAccountKey &&
      previousAccountKey !== nextSummary?.accountKey
        ? buildProductAutoPostSummaryDeleteUpdates(
            userId,
            postId,
            previousAccountKey
          )
        : {};
    const cronEligible = await getExistingPostCronEligibility(
      database,
      userId,
      postId
    );

    return update(
      ref(database),
      {
        ...staleSummaryDeleteUpdates,
        ...buildPostConfigUpsertUpdates(userId, postId, config, {
          cronEligible,
        }),
      }
    );
  }

  const postRef = push(ref(database, `posts/${userId}`));
  await update(
    ref(database),
    buildPostConfigUpsertUpdates(userId, postRef.key, config)
  );
  return postRef;
};

export const removePostConfigWithCronIndex = async (database, userId, postId) => {
  const summaryRefSnapshot = await get(
    ref(database, `${PRODUCT_AUTO_POST_CONFIG_SUMMARY_REF_ROOT}/${userId}/${postId}`)
  ).catch(() => null);
  const accountKey = summaryRefSnapshot?.exists()
    ? summaryRefSnapshot.val()?.accountKey
    : null;

  return update(ref(database), {
    [`posts/${userId}/${postId}`]: null,
    ...buildPostCronIndexDeleteUpdates(userId, postId),
    ...buildProductAutoPostSummaryDeleteUpdates(userId, postId, accountKey),
  });
};
